
import React, { useState, useMemo } from 'react';
import { Task, TaskStatus, Quadrant } from '../types';
import { Button } from '../components/Button';
import { Check, Trash2, Plus, Calendar, RefreshCw, Trophy, Clock } from 'lucide-react';
import { FREE_ROUTINE_LIMIT } from '../constants';

interface DailyViewProps {
  tasks: Task[];
  onAddTask: (title: string, quadrant: Quadrant, isRoutine: boolean) => void;
  onUpdateStatus: (taskId: string, status: TaskStatus) => void;
  onDeleteTask: (id: string) => void;
  isPro: boolean;
  onUpgrade: () => void;
}

export const DailyView: React.FC<DailyViewProps> = ({ 
  tasks, 
  onAddTask, 
  onUpdateStatus, 
  onDeleteTask,
  isPro,
  onUpgrade
}) => {
  const [routineInput, setRoutineInput] = useState('');

  // --- LOGIC: DATE HANDLING ---
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayTimestamp = todayStart.getTime();

  // --- LOGIC: ROUTINES ---
  const routines = useMemo(() => {
    return tasks.filter(t => t.isRoutine);
  }, [tasks]);

  const isRoutineDoneToday = (task: Task) => {
    return task.status === TaskStatus.Done && (task.completedAt || 0) >= todayTimestamp;
  };

  const handleToggleRoutine = (task: Task) => {
    const isDone = isRoutineDoneToday(task);
    onUpdateStatus(task.id, isDone ? TaskStatus.Todo : TaskStatus.Done);
  };

  const handleAddRoutine = (e: React.FormEvent) => {
    e.preventDefault();
    if (!routineInput.trim()) return;
    
    // Enforce Routine Limit for Free Users
    if (!isPro && routines.length >= FREE_ROUTINE_LIMIT) {
      onUpgrade();
      return;
    }

    onAddTask(routineInput, Quadrant.Schedule, true);
    setRoutineInput('');
  };

  // --- LOGIC: COMPLETED LOG ---
  const completedToday = useMemo(() => {
    return tasks
      .filter(t => t.status === TaskStatus.Done && (t.completedAt || 0) >= todayTimestamp)
      .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0));
  }, [tasks, todayTimestamp]);

  const formatTime = (ts: number) => {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 h-full pb-4 animate-fade-in">
      
      {/* COLUMN 1: DAILY RITUALS (ROUTINES) */}
      <div className="flex-1 bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border flex flex-col overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
           <div className="flex items-center gap-2">
             <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded-md">
                <RefreshCw size={18} />
             </div>
             <div>
               <h3 className="font-bold text-gray-800 dark:text-gray-200">Daily Rituals</h3>
               <p className="text-[10px] text-gray-500 uppercase tracking-wide">Reset daily</p>
             </div>
           </div>
           <div className="text-xs font-medium text-gray-400">
              {routines.filter(isRoutineDoneToday).length} / {routines.length}
           </div>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-2">
           {routines.map(routine => {
             const isDone = isRoutineDoneToday(routine);
             return (
               <div 
                 key={routine.id} 
                 onClick={() => handleToggleRoutine(routine)}
                 className={`group flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                   isDone 
                     ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/30' 
                     : 'bg-white dark:bg-dark-bg border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                 }`}
               >
                 <div className="flex items-center gap-3">
                   <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${
                     isDone ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 dark:border-gray-600'
                   }`}>
                     {isDone && <Check size={14} strokeWidth={3} />}
                   </div>
                   <span className={`text-sm font-medium ${
                     isDone ? 'text-gray-500 line-through' : 'text-gray-800 dark:text-gray-200'
                   }`}>
                     {routine.title}
                   </span>
                 </div>
                 
                 <button 
                   onClick={(e) => { e.stopPropagation(); onDeleteTask(routine.id); }}
                   className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                 >
                   <Trash2 size={14} />
                 </button>
               </div>
             );
           })}

           {routines.length === 0 && (
             <div className="text-center py-8 text-gray-400 text-sm">
               No daily rituals yet. <br/> Add habits you want to track every day.
             </div>
           )}
        </div>

        <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
           <form onSubmit={handleAddRoutine} className="relative">
             <input 
               type="text" 
               placeholder={!isPro && routines.length >= FREE_ROUTINE_LIMIT ? "Ritual limit reached" : "Add new ritual..."} 
               value={routineInput}
               disabled={!isPro && routines.length >= FREE_ROUTINE_LIMIT}
               onChange={(e) => setRoutineInput(e.target.value)}
               className="w-full pl-3 pr-10 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
             />
             <button 
               type="submit"
               disabled={!routineInput.trim() || (!isPro && routines.length >= FREE_ROUTINE_LIMIT)}
               className="absolute right-1.5 top-1.5 p-1 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50"
             >
               <Plus size={16} />
             </button>
           </form>
           {!isPro && routines.length >= FREE_ROUTINE_LIMIT && (
             <p className="text-[10px] text-orange-600 mt-2 font-medium text-center">
               Free plan limit: {FREE_ROUTINE_LIMIT} rituals. Upgrade for unlimited.
             </p>
           )}
        </div>
      </div>

      {/* COLUMN 2: ACCOMPLISHED TODAY */}
      <div className="flex-1 bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border flex flex-col overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
           <div className="flex items-center gap-2">
             <div className="p-1.5 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-md">
                <Trophy size={18} />
             </div>
             <div>
               <h3 className="font-bold text-gray-800 dark:text-gray-200">Accomplished Today</h3>
               <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                 {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
               </p>
             </div>
           </div>
           <div className="text-xl font-bold text-green-600">{completedToday.length}</div>
        </div>

        <div className="flex-1 overflow-y-auto p-0">
           {completedToday.map((task, index) => {
             let badgeColor = "bg-gray-100 text-gray-600";
             let badgeLabel = "TASK";
             
             if (task.isRoutine) {
               badgeColor = "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400";
               badgeLabel = "RITUAL";
             } else {
               badgeLabel = task.quadrant.substring(0, 1) + task.quadrant.substring(1).toLowerCase();
               if(task.quadrant === Quadrant.Do) badgeColor = "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
               if(task.quadrant === Quadrant.Schedule) badgeColor = "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
               if(task.quadrant === Quadrant.Delegate) badgeColor = "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
               if(task.quadrant === Quadrant.Eliminate) badgeColor = "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
             }

             return (
               <div key={task.id} className="flex items-center gap-4 p-4 border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <div className="text-xs font-mono text-gray-400 flex flex-col items-center min-w-[50px]">
                     <Clock size={12} className="mb-0.5" />
                     {task.completedAt ? formatTime(task.completedAt) : '--:--'}
                  </div>
                  
                  <div className="flex-1">
                     <div className="flex items-center gap-2 mb-0.5">
                       <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${badgeColor}`}>
                         {badgeLabel}
                       </span>
                     </div>
                     <p className="text-sm font-medium text-gray-800 dark:text-gray-200 line-through opacity-70">
                       {task.title}
                     </p>
                  </div>

                  <button 
                    onClick={() => onUpdateStatus(task.id, TaskStatus.Todo)}
                    className="text-gray-300 hover:text-blue-500 p-1"
                    title="Undo complete"
                  >
                    <RefreshCw size={14} />
                  </button>
               </div>
             )
           })}

           {completedToday.length === 0 && (
             <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm pb-10">
               <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4 text-gray-300">
                  <Trophy size={32} />
               </div>
               <p>Nothing completed yet today.</p>
               <p className="text-xs mt-1">Check off a routine or complete a matrix task!</p>
             </div>
           )}
        </div>
      </div>

    </div>
  );
};
